import React, { useState, useEffect, useMemo } from 'react'
import { Menu } from 'antd'
import { Link } from 'react-router-dom'
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons'

import { useAppDispatch, useAppSelector } from '@/utils/hooks'

import type { MenuProps } from 'antd'
import { updateCollapsed } from '@/redux/reducers/app'

interface Props {
  routes: Routes
}

type MenuItem = Required<MenuProps>['items'][number]

const getItem = (
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
  type?: 'group'
): MenuItem => {
  return {
    label,
    key,
    icon,
    children,
    type,
  } as MenuItem
}

const themeEnum = {
  white: 'light',
  black: 'dark',
  blue: 'dark',
}

const manageAsideCollapsedColor = {
  white: '#10131a',
  black: '#fff',
  blue: '#fff',
}

const menuStyleId = {
  white: 'menu-main-white',
  black: 'menu-main-black',
  blue: 'menu-main-blue',
}

const SideMenuAside: React.FC<Props> = ({ routes }) => {
  const { uiConfig, collapsed: appCollapsed } = useAppSelector(state => state.app)
  const dispatch = useAppDispatch()

  const path = location.hash.replace('#', '')
  const [openKeys, setOpenKeys] = useState<string[]>()
  const [selectedKeys, setSelectedKeys] = useState<string[]>()
  const [collapsed, setCollapsed] = useState(appCollapsed || false)

  useEffect(() => {
    const urlKey = /[A-z, /]+/.exec(path) || []
    const menuPath = /\/(\w+)\/(\w+)/.exec(path) || []
    setSelectedKeys([urlKey[0]] as string[])
    setOpenKeys([menuPath[0]] as string[])
  }, [path])

  const items = useMemo(() => {
    const mapItems: any = (routes: Routes) => {
      return routes.map(route => {
        if (route.name) {
          return getItem(
            route.routes ? route.name : <Link to={route.path}>{route.name}</Link>,
            route.path,
            route.icon,
            route.routes ? mapItems(route.routes) : undefined
          )
        }
      })
    }
    return mapItems(routes)
  }, [routes])

  const toggleCollapsed = () => {
    setCollapsed(!collapsed)
    dispatch(updateCollapsed(!collapsed))
  }

  const menuCollapsedStyle = () => {
    return uiConfig.menuBarLayout
      ? {
          // @ts-ignore
          color: manageAsideCollapsedColor[uiConfig.sidebarColor as any] as any,
          marginLeft: 10,
          paddingTop: 10,
          display: 'inline-block',
          fontSize: 20,
        }
      : {}
  }

  return (
    <div className="manage-aside">
      <span onClick={toggleCollapsed} className="manage-aside-collapsed">
        {collapsed ? (
          <MenuUnfoldOutlined style={menuCollapsedStyle()} />
        ) : (
          <MenuFoldOutlined style={menuCollapsedStyle()} />
        )}
      </span>
      <Menu
        theme={
          uiConfig.menuBarLayout === 2
            ? (themeEnum[uiConfig.sidebarColor as keyof typeof themeEnum] as any)
            : ('light' as any)
        }
        className={menuStyleId[uiConfig.sidebarColor as keyof typeof menuStyleId]}
        id={menuStyleId[uiConfig.sidebarColor as keyof typeof menuStyleId]}
        selectedKeys={selectedKeys}
        openKeys={openKeys}
        mode="inline"
        inlineCollapsed={collapsed}
        style={{ width: collapsed ? 80 : 200 }}
        onOpenChange={keys => {
          setOpenKeys(keys.slice(-1) as string[])
        }}
        items={items}
      ></Menu>
    </div>
  )
}

export default SideMenuAside
